import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  NumberInput,
  Textarea,
  Button,
  Group,
  Text,
  Alert,
  Paper,
  Radio,
  Box,
} from "@mantine/core";
import { IconAlertCircle, IconArrowLeft } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { useProducts } from "../../context/ProductsContext";
import styles from "./AdjustQuantityModal.module.css";

interface AdjustQuantityModalProps {
  opened: boolean;
  onClose: () => void;
  productId: string;
  productName: string;
  onSuccess: () => void;
}

type AdjustmentMode = "set-total" | "add-remove" | null;

export default function AdjustQuantityModal({
  opened,
  onClose,
  productId,
  productName,
  onSuccess,
}: AdjustQuantityModalProps) {
  const { createProductStockAdjustment } = useApp();
  const { products } = useProducts();
  const { isMobile } = useResponsive();

  // Find current product to get current stock
  const currentProduct = products.find((p) => p.id === productId);
  const currentStock = currentProduct?.quantity ?? 0;

  // Step 1: Mode selection
  const [selectedMode, setSelectedMode] = useState<string>("");
  const [adjustmentMode, setAdjustmentMode] = useState<AdjustmentMode>(null);

  // Step 2: Input values
  const [newTotalStock, setNewTotalStock] = useState<number | "">(currentStock);
  const [adjustmentAmount, setAdjustmentAmount] = useState<number | "">(0);
  const [notes, setNotes] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync newTotalStock with currentStock when modal opens or currentStock changes
  useEffect(() => {
    if (opened) {
      // Reset state when modal opens
      setNewTotalStock(currentStock);
      setAdjustmentAmount(0);
      setNotes("");
      setSelectedMode("");
      setAdjustmentMode(null);
      setError(null);
    }
  }, [opened, currentStock]);

  const handleClose = () => {
    if (!isSubmitting) {
      // Reset all state
      setSelectedMode("");
      setAdjustmentMode(null);
      setNewTotalStock(currentStock);
      setAdjustmentAmount(0);
      setNotes("");
      setError(null);
      onClose();
    }
  };

  const handleNext = () => {
    if (selectedMode === "set-total" || selectedMode === "add-remove") {
      setAdjustmentMode(selectedMode);
      // Sync newTotalStock with current stock when transitioning to set-total mode
      if (selectedMode === "set-total") {
        setNewTotalStock(currentStock);
      }
    }
  };

  const handleBack = () => {
    setAdjustmentMode(null);
    setSelectedMode("");
    setError(null);
  };

  const calculateDelta = (): number => {
    if (adjustmentMode === "set-total") {
      const targetStock = typeof newTotalStock === "number" ? newTotalStock : 0;
      return targetStock - currentStock;
    } else if (adjustmentMode === "add-remove") {
      return typeof adjustmentAmount === "number" ? adjustmentAmount : 0;
    }
    return 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const delta = calculateDelta();

    if (delta === 0) {
      setError("Adjustment amount cannot be zero");
      return;
    }

    // Validate that set-total doesn't result in negative stock
    if (adjustmentMode === "set-total") {
      const targetStock = typeof newTotalStock === "number" ? newTotalStock : 0;
      if (targetStock < 0) {
        setError("Stock cannot be negative");
        return;
      }
    }

    // Validate that add-remove doesn't result in negative stock
    if (adjustmentMode === "add-remove") {
      const resultingStock = currentStock + delta;
      if (resultingStock < 0) {
        setError(
          `Cannot remove ${Math.abs(delta)} units. Only ${currentStock} units available.`,
        );
        return;
      }
    }

    try {
      setIsSubmitting(true);
      setError(null);

      await createProductStockAdjustment(productId, {
        adjustment: delta,
        notes: notes.trim() || undefined,
      });

      handleClose();
      onSuccess();
    } catch (err) {
      console.error("Error creating stock adjustment:", err);
      setError(err instanceof Error ? err.message : "Failed to adjust stock");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 1: Mode Selection Screen
  if (!adjustmentMode) {
    return (
      <Modal
        opened={opened}
        onClose={handleClose}
        title="Adjust Stock"
        size="md"
        fullScreen={isMobile}
        centered
      >
        <Stack gap="md">
          <Text c="var(--text-primary)">
            How would you like to adjust the stock?
          </Text>

          <Radio.Group value={selectedMode} onChange={setSelectedMode}>
            <Stack gap="md">
              <Paper
                p="md"
                withBorder
                className={styles.optionCard}
                onClick={() => setSelectedMode("set-total")}
              >
                <div className={styles.radioWrapper}>
                  <Radio value="set-total" />
                  <div className={styles.radioContent}>
                    <Text className={styles.radioTitle}>Set Total Stock</Text>
                    <Text className={styles.radioDescription}>
                      When you know exactly how much stock you have on hand.
                    </Text>
                    <Text className={styles.radioExample}>
                      Ex: Inventory recount shows 7 units instead of the 11 in
                      the system
                    </Text>
                  </div>
                </div>
              </Paper>

              <Paper
                p="md"
                withBorder
                className={styles.optionCard}
                onClick={() => setSelectedMode("add-remove")}
              >
                <div className={styles.radioWrapper}>
                  <Radio value="add-remove" />
                  <div className={styles.radioContent}>
                    <Text className={styles.radioTitle}>Add/Remove Stock</Text>
                    <Text className={styles.radioDescription}>
                      When units are added or removed from the inventory.
                    </Text>
                    <Text className={styles.radioExample}>
                      Ex: New shipment of 20 units, accidental loss of 1 unit
                    </Text>
                  </div>
                </div>
              </Paper>
            </Stack>
          </Radio.Group>

          <Group justify="space-between" mt="md">
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleNext} disabled={!selectedMode}>
              Next
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  // Step 2: Input Screen
  const delta = calculateDelta();
  const resultingStock = currentStock + delta;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        adjustmentMode === "set-total" ? "Set Total Stock" : "Add/Remove Stock"
      }
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Error"
              color="red"
            >
              {error}
            </Alert>
          )}

          {adjustmentMode === "set-total" ? (
            <NumberInput
              label="New Total Stock"
              description="Enter the exact number of units you want in stock"
              placeholder="e.g., 7"
              value={newTotalStock}
              onChange={(value) => {
                if (typeof value === "number") {
                  setNewTotalStock(value);
                } else if (value === "") {
                  setNewTotalStock("");
                }
              }}
              required
              allowDecimal={false}
              min={0}
              autoFocus
            />
          ) : (
            <NumberInput
              label="Adjustment Amount"
              description="Enter positive number to add stock, negative to remove stock"
              placeholder="e.g., 20 or -1"
              value={adjustmentAmount}
              onChange={(value) => {
                if (typeof value === "number") {
                  setAdjustmentAmount(value);
                } else if (value === "") {
                  setAdjustmentAmount("");
                }
              }}
              required
              allowDecimal={false}
              min={undefined}
              autoFocus
            />
          )}

          {/* Preview of change */}
          {delta !== 0 && (
            <Paper p="sm" withBorder className={styles.previewCard}>
              <Text>
                {currentStock} units (current) → {resultingStock} units (new)
              </Text>
            </Paper>
          )}

          <Textarea
            label="Notes"
            placeholder={
              adjustmentMode === "set-total"
                ? "e.g., Inventory recount"
                : "e.g., Received shipment, accidental damage"
            }
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            minRows={3}
            maxRows={6}
          />

          <Group justify="space-between" mt="md">
            <Button
              variant="subtle"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Group gap="sm">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button type="submit" loading={isSubmitting}>
                Adjust Stock
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
